import { LayersList } from '@deck.gl/core/typed';
import { Box, ClickAwayListener, styled, Tooltip } from '@mui/material';
import { LocationPin } from '@styled-icons/entypo';
import { featureCollection, point, validateBBox } from '@turf/helpers';
import { BBox2d } from '@turf/helpers/dist/js/lib/geojson';
import { bbox, MultiPolygon, Point, Polygon, Position } from '@turf/turf';
import ThemeButton from 'DesignSystem/Buttons/ThemeButton/ThemeButton';
import Icon, { IconColor, TIconName } from 'DesignSystem/Primitives/Icon/Icon';
import { Feature, FeatureCollection, Geometry } from 'geojson';
import { Anchor } from 'mapbox-gl';
import useUserData from 'FeatureModules/Auth/hooks/useUserData';
import React, {
  ComponentProps,
  FC,
  ReactNode,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import MapGL, {
  FullscreenControl,
  MapEvent,
  Marker,
  MarkerDragEvent,
  NavigationControl,
  Popup,
  ScaleControl,
} from 'react-map-gl';
import { MapCallbacks } from 'react-map-gl/dist/esm/types/events-mapbox';
import { v4 as uuid } from 'uuid';
import DeckGLOverlay from './utils/DeckOverlay';
import DrawControl from './utils/DrawControl';
import { MAP_STYLES, MAX_AUTO_ZOOM_LEVEL, MAX_FOCUS_ANIMATION_TIME } from './utils/map.config';

export type TMapStyle = 'default' | 'satellite' | 'outdoors';

export interface IMapPopup {
  id?: string;
  coordinate: Position;
  content: ReactNode;
}

export interface IMapMarkerOptions {
  customIcon?: TIconName;
  /**
   * Icon color from type or hex / string value
   * Please prefer to use the IconColor type and ONLY use string if you need a custom color (e.g. hex value from a palette)
   */
  color?: IconColor | string;
  anchor?: Anchor;
  draggable?: boolean;
  /** @default true */
  focusOnClick?: boolean;
}

export interface IMapMarker {
  id?: string;
  coordinate: Position;
  tooltip?: string;
  /** @default anchor is center */
  options?: IMapMarkerOptions;
}

export interface IMapMarkerClickEvent {
  id?: string;
  coordinate: Position;
}

export interface ICustomMapRef {
  centerFeatures: () => void;
  customFitBounds: (bounds: [number, number, number, number]) => void;
  exitFullscreen: () => void;
}

/**
 * Only expose the properties that are really needed and used for the component
 */
export interface ICustomMapProps
  extends Pick<
      ComponentProps<typeof MapGL>,
      'style' | 'initialViewState' | 'longitude' | 'latitude' | 'zoom'
    >,
    MapCallbacks {
  infoPopup?: IMapPopup;
  /** The features that should be drawn on the map initially */
  defaultDrawFeatures?: Feature | FeatureCollection | Geometry;
  /** You can pass a set of deck gl layer that will be rendered on top of the map */
  layers?: LayersList | undefined;
  mapStyle?: TMapStyle;
  /** An array of lat, lng position where a marker will be placed on the map
   * Warning this are x, y coordinates [lng, lat]
   * The marker is currently always draggable
   */
  markers?: (IMapMarker | Position)[];

  /**
   * Set the default options for all the markers
   * Will be overridden if the marker itself has options set
   */
  markerOptions?: IMapMarkerOptions;

  /** @default false */
  config?: {
    /** @default false */
    disableControls?: boolean;
    /** @default false */
    enableCenterButton?: boolean;
    /** @default 'Center plot(s)' */
    centerButtonText?: string;
    /** @default false */
    enableDrawing?: boolean;
    /** @default false */
    enableMapStyleToggle?: boolean;
    /** @default false */
    disableFullscreenControl?: boolean;
    /** @default false - Hide drawing buttons (polygon/point) but keep edit/delete functionality */
    hideDrawingButtons?: boolean;
  };

  /** Reference to the component itself to call function inside of it */
  customRef?: Ref<ICustomMapRef>;

  onDrawCreate?: (event: Feature<Point | Polygon | MultiPolygon> | undefined) => void;
  onDrawUpdate?: (event: Feature<Point | Polygon | MultiPolygon> | undefined) => void;
  onDrawDelete?: (event: Feature<Point | Polygon | MultiPolygon> | undefined) => void;
  onMarkerDragEnd?: (event: MarkerDragEvent) => void;
  onMarkerClick?: (event: IMapMarkerClickEvent) => void;
  onMarkerPopupClose?: () => void;
  onMapLoad?: (map: MapEvent['target']) => void;
}

const StyledLocationPin = styled(LocationPin)(({ theme }) => ({
  color: theme.palette.primary.main,
}));

const StyledNavigationControl = styled(NavigationControl)(({ theme }) => ({
  right: 10,
  top: 10,
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(5),
  right: theme.spacing(1),
  zIndex: 9,
}));

const HoverContainer = styled('div')<{ hoverEnabled: boolean }>(({ theme, hoverEnabled }) => ({
  ':hover': {
    cursor: hoverEnabled ? 'pointer' : 'grab',
  },
}));

export const CustomMap: FC<ICustomMapProps> = ({
  customRef,
  defaultDrawFeatures,
  markers,
  markerOptions,
  infoPopup,
  mapStyle = 'default',
  layers,
  initialViewState,
  config,
  style,
  onMapLoad,
  onDrawCreate,
  onDrawUpdate,
  onDrawDelete,
  onMarkerDragEnd,
  onMarkerClick,
  onMarkerPopupClose,
  ...props
}) => {
  const { company } = useUserData();
  const mapboxToken =
    company?.useDefaultMapboxKey === false
      ? company?.mapboxApiToken
      : company?.mapboxApiToken || process.env.REACT_APP_MAP_BOX_TOKEN;

  const [currentMapStyle, setCurrentMapStyle] = useState<TMapStyle>(mapStyle || 'satellite');
  /**
   * For some reason the map ref is always null initially, so we need to store it in a state
   */
  const [mapRef, setMapRef] = useState<MapEvent['target'] | null>(null);
  const [drawRef, setDrawRef] = useState<MapboxDraw | null>(null);
  const [isCursorHoveringLayer, setIsCursorHoveringLayer] = useState<boolean>(false);

  useImperativeHandle(customRef, () => ({
    centerFeatures,
    customFitBounds,
    exitFullscreen,
  }));

  useEffect(() => {
    if (defaultDrawFeatures && drawRef) {
      drawRef?.add(defaultDrawFeatures);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawRef]);

  /**
   * Zoom into bounds of the features initially when the map is loaded and features are present
   */
  useEffect(() => {
    if (mapRef && (layers || defaultDrawFeatures)) {
      // The container is not sized when the map initializes, the map needs to be manually resized first
      mapRef.resize(); // ensure dimensions are correct
      mapRef.once('idle', () => {
        centerFeatures();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef]);

  const exitFullscreen = useCallback(() => {
    try {
      // Make sure to exit fullscreen mode if the document is in fullscreen
      if (document.fullscreenElement) {
        document.exitFullscreen();
      }
    } catch (e) {
      // ignore errors, e.g. if the document is not in fullscreen mode
    }
  }, []);

  /**
   * Workaround to fix the reference not being updated when the ref is passed properly
   * This is a known issue with react-map-gl in some situation but it is still unclear why it work for some of our components
   * https://stackoverflow.com/questions/71224100/react-ref-current-is-still-null-in-componentdidupdate
   * Get rid of this when the issue is fixed and use the ref directly
   */
  const updateMapRef = useCallback(
    (event: MapEvent) => {
      setMapRef(event.target);
      onMapLoad?.(event.target);
    },
    [onMapLoad]
  );

  /**
   * Handle the cursor when hovering a layer
   * Warning: This is a extremely performance heavy function
   */
  const handleCursorHoveringLayer = useCallback(
    (event: { isHovering: boolean }) => {
      setIsCursorHoveringLayer(event.isHovering);
      return event.isHovering ? 'grab' : 'pointer';
    },
    [setIsCursorHoveringLayer]
  );

  const toggleMapStyle = () => {
    if (currentMapStyle === 'default') {
      setCurrentMapStyle('outdoors');
    } else if (currentMapStyle === 'outdoors') {
      setCurrentMapStyle('satellite');
    } else {
      setCurrentMapStyle('default');
    }
  };

  const customFitBounds = useCallback(
    (bounds: [number, number, number, number]) => {
      if (!mapRef) {
        return;
      }
      try {
        validateBBox(bounds); // will throw if the bbox is invalid
      } catch (error) {
        console.error('Invalid bounds provided to fitBounds:', bounds);
        return;
      }
      mapRef.fitBounds(bounds, {
        padding: 40,
        maxDuration: MAX_FOCUS_ANIMATION_TIME,
        animate: true,
        maxZoom: MAX_AUTO_ZOOM_LEVEL,
      });
    },
    [mapRef]
  );

  /**
   * Close the popup if the backdrop is clicked
   * Since this event has bubbling enable and all map click events are still triggered we need to make sure the popup is closed after all other events
   * Therefor we use the setTimeout, otherwise the click event on features would trigger & set the popup to open again (to open again the user needs 2 click)
   */
  const handlePopupClose = () => setTimeout(() => onMarkerPopupClose?.(), 0);

  /**
   * Note: For now this functions assumes for layer features, that all features are stored in the first layer
   */
  const centerFeatures = useCallback(
    (preventMarkerFocus: boolean = false) => {
      if (!mapRef) {
        return;
      }

      const allFeatures = [];

      if (config?.enableDrawing && drawRef?.getAll().features.length) {
        allFeatures.push(...drawRef.getAll().features);
      } else if (
        layers?.length &&
        layers[0] &&
        'state' in layers[0] &&
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (layers[0].props.data as any)?.features?.length
      ) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        allFeatures.push(...(layers[0].props.data as any).features);
      }

      if (markers?.length && !preventMarkerFocus) {
        const markerFeatures = markers.map(marker => {
          const coordinate = Array.isArray(marker) ? marker : marker.coordinate;
          return point(coordinate);
        });
        allFeatures.push(...markerFeatures);
      }

      if (allFeatures.length) {
        const combinedFeatureCollection = featureCollection(allFeatures);
        customFitBounds(bbox(combinedFeatureCollection) as BBox2d);
      }
    },
    [mapRef, drawRef, layers, markers, config?.enableDrawing, customFitBounds]
  );

  /**
   * Reset the draw mode when clicking outside of the map (e.g. on the popup close)
   */
  const handleClickAway = (e: MouseEvent | TouchEvent) => {
    try {
      // Workaround to not trigger the click away when clicking on the map itself (e.g. changing drawings)
      if ((e.srcElement as HTMLElement)?.className?.includes('mapboxgl-canvas')) return;
      onMarkerPopupClose?.();
      drawRef?.changeMode('simple_select'); // reset draw mode
    } catch (error) {}
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <MapGL
        mapboxAccessToken={mapboxToken}
        mapStyle={MAP_STYLES[currentMapStyle]}
        // Initial view state is set to the center of the world
        initialViewState={{ zoom: 2, latitude: 20, longitude: 25, ...initialViewState }}
        {...props}
        style={{ borderRadius: 4, ...style }}
        cursor={isCursorHoveringLayer ? 'pointer' : undefined}
        onLoad={updateMapRef}
      >
        {infoPopup && (
          <Popup
            longitude={infoPopup.coordinate[0]}
            latitude={infoPopup.coordinate[1]}
            // Zoom into the popup coordinate when it opens
            onOpen={() =>
              mapRef?.flyTo({
                center: { lng: infoPopup.coordinate[0], lat: infoPopup.coordinate[1] },
                padding: 40,
                animate: true,
                zoom: MAX_AUTO_ZOOM_LEVEL,
                maxDuration: MAX_FOCUS_ANIMATION_TIME,
              })
            }
            focusAfterOpen={true}
            closeButton={false}
            maxWidth="420px"
            // Raise popup to the top of the map and layers
            style={{ zIndex: 1000 }}
            onClose={handlePopupClose}
          >
            {infoPopup.content}
          </Popup>
        )}

        {/* Layer overlay */}
        {layers && <DeckGLOverlay layers={layers} getCursor={handleCursorHoveringLayer} />}

        {/* Markers */}
        {markers &&
          markers.map(marker => {
            let _marker: IMapMarker;
            if (Array.isArray(marker)) {
              _marker = { options: markerOptions, coordinate: marker };
            } else {
              _marker = { options: markerOptions, ...marker };
            }

            const handleClickMarker = (e: { originalEvent: { stopPropagation: () => void } }) => {
              e.originalEvent.stopPropagation();
              onMarkerClick?.({ id: _marker.id, coordinate: _marker.coordinate });
              if (_marker.options?.focusOnClick !== false) {
                mapRef?.flyTo({
                  center: { lng: _marker.coordinate[0], lat: _marker.coordinate[1] },
                  padding: 40,
                  animate: true,
                  zoom: MAX_AUTO_ZOOM_LEVEL,
                  maxDuration: MAX_FOCUS_ANIMATION_TIME,
                });
              }
            };

            const MarkerContent = (
              <HoverContainer hoverEnabled={!!handleClickMarker}>
                {_marker.options?.customIcon ? (
                  <Icon
                    name={_marker.options.customIcon}
                    color={_marker.options.color}
                    size="x-large"
                  />
                ) : (
                  <StyledLocationPin size={50} />
                )}
              </HoverContainer>
            );

            return (
              <Marker
                key={uuid()}
                longitude={_marker.coordinate[0]}
                latitude={_marker.coordinate[1]}
                draggable={_marker.options?.draggable}
                anchor={_marker.options ? _marker.options.anchor || 'center' : 'bottom'}
                onDragEnd={onMarkerDragEnd}
                onClick={handleClickMarker}
              >
                {_marker.tooltip ? (
                  <Tooltip title={_marker.tooltip}>{MarkerContent}</Tooltip>
                ) : (
                  MarkerContent
                )}
              </Marker>
            );
          })}

        {/* Needs to be enabled to draw polygons on the map regardless of the drawing feature */}
        {(config?.enableDrawing || defaultDrawFeatures) && (
          <DrawControl
            position="top-left"
            displayControlsDefault={false}
            hideDrawingButtons={config?.hideDrawingButtons}
            onDrawControlInit={setDrawRef}
            onCreate={onDrawCreate}
            onUpdate={onDrawUpdate}
            onDelete={onDrawDelete}
          />
        )}

        {/* Map controls */}
        {!config?.disableControls && <StyledNavigationControl showCompass={false} />}
        {!config?.disableFullscreenControl && <FullscreenControl />}

        <ButtonContainer gap={1} display="flex" alignItems="center">
          {config?.enableCenterButton && (
            <ThemeButton
              startIcon={<Icon size="small" name="focus" />}
              color="WHITE"
              size="small"
              onClick={() => centerFeatures(true)}
            >
              {config.centerButtonText || 'Center plot(s)'}
            </ThemeButton>
          )}
          {config?.enableMapStyleToggle && (
            <ThemeButton
              startIcon={<Icon size="small" name="payments" />}
              color="WHITE"
              size="small"
              onClick={toggleMapStyle}
            >
              Map style
            </ThemeButton>
          )}
        </ButtonContainer>
        <ScaleControl />
      </MapGL>
    </ClickAwayListener>
  );
};

export default CustomMap;
